// Helper function to extract judge name with multiple fallback strategies
import { IUserInterface } from '../../../../models/users/user.interface'

export const getJudgeName = (judge: any, judges: IUserInterface[], judgeIndex: number): string => {
  let judgeUser = judges.find((user) => user.id === judge.id)

  // If not found by ID, try matching by index
  if (!judgeUser && judges[judgeIndex]) {
    judgeUser = judges[judgeIndex]
  }

  // Try alternative ID matching strategies
  if (!judgeUser) {
    judgeUser = judges.find(
      (user) => user.uid === judge.id || user._id === judge.id || user.userId === judge.id
    )
  }

  let judgeName = `Judge ${judgeIndex + 1}` // Default fallback

  if (judgeUser) {
    // Try different name combinations
    const firstName = judgeUser.firstName || judgeUser.userFirstName || judgeUser.first_name || ''
    const lastName = judgeUser.lastName || judgeUser.userLastName || judgeUser.last_name || ''
    const fullName = judgeUser.name || judgeUser.userFullName || judgeUser.fullName || ''
    const displayName = judgeUser.displayName || ''

    if (firstName || lastName) {
      judgeName = `${firstName} ${lastName}`.trim()
    } else if (fullName) {
      judgeName = fullName
    } else if (displayName) {
      judgeName = displayName
    }
  } else if (judge.name) {
    // Check if judge object itself has name
    judgeName = judge.name
  } else if (judge.judgeName) {
    // Check for judgeName property
    judgeName = judge.judgeName
  } else if (judge.user) {
    // Check if there's a nested user object
    const judgeUserData = judge.user
    const firstName = judgeUserData.firstName || judgeUserData.first_name || ''
    const lastName = judgeUserData.lastName || judgeUserData.last_name || ''
    if (firstName || lastName) {
      judgeName = `${firstName} ${lastName}`.trim()
    } else if (judgeUserData.name) {
      judgeName = judgeUserData.name
    }
  }

  return judgeName
}

// Helper function to calculate judge scores and percentages
export const calculateJudgeScores = (judge: any) => {
  let judgeTotal = 0
  let judgeMax = 0
  let individualTasksMax = 0

  if (judge.sections) {
    judge.sections.forEach((section: any) => {
      section.rows.forEach((row: any) => {
        if (row.coefficient) {
          judgeTotal += Number(row.coefficient) * Number(row.score || 0)
        } else if (row.fraction === 'minus') {
          judgeTotal -= Number(row.score || 0)
        } else {
          judgeTotal += Number(row.score || 0)
        }
      })
    })
    judgeTotal -= judge.errors || 0

    // Calculate total max (all sections)
    judge.sections.forEach((section: any) => {
      judgeMax += section.max || 0
    })

    // Calculate individual tasks max (excluding collective marks)
    judge.sections.forEach((section: any) => {
      const isCollectiveMarks =
        section.name &&
        (section.name.toLowerCase().includes('collective') ||
          section.name.toLowerCase().includes('marks') ||
          section.type === 'collective' ||
          section.type === 'score')

      if (!isCollectiveMarks) {
        individualTasksMax += section.max || 0
      }
    })
  }

  const judgePercentage = judgeMax > 0 ? ((judgeTotal / judgeMax) * 100).toFixed(3) : '0.000'

  return {
    judgeTotal,
    judgeMax,
    individualTasksMax,
    judgePercentage,
  }
}

// Helper function to wrap text within specified width
export const wrapText = (text: string, maxWidth: number, fontSize: number, font: any): string[] => {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word
    const textWidth = font.widthOfTextAtSize(testLine, fontSize)

    if (textWidth <= maxWidth) {
      currentLine = testLine
    } else {
      if (currentLine) {
        lines.push(currentLine)
        currentLine = word
      } else {
        // Single word is too long, force it on a line
        lines.push(word)
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines.length > 0 ? lines : [text]
}
